import axios from "axios";
import { WebAPI, IFailCallbackParam, ISelectSimulatorParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIでシミュレータ削除が成功した場合に呼び出されます
   * @callback success
   * @param {number} status - httpステータス
   */
  success: (status: number) => void;
}

function AxiosDelete(url: string, token?: string) {
  if (token) {
    return axios.delete(url, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.delete(url);
  }
}

export interface IDeleteSimulatorsParams extends ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * 選択シミュレータパラメータ配列
   */
  simulators: ISelectSimulatorParam[];
}

/**
 * シミュレータを削除します
 * 基本的にはdeleteProjectsと同じ作りで、パラメータからプロジェクトIDとシミュレータIDを抜き出しています
 * @function deleteSimulators
 * @param {IDeleteSimulatorsParams} params - シミュレータ削除パラメータ
 */
export function deleteSimulators(params: IDeleteSimulatorsParams): void {
  const { authService, simulators } = params;
  if (authService) {
    const promises = simulators.map((simulator) => {
      const { projectid, simulatorid } = simulator;
      const url = WebAPI.DELETE_SIMULATOR(projectid, simulatorid);
      return new Promise((resolve, reject) => {
        authService.getToken({
          success: (token) => {
            AxiosDelete(url, token)
            .then((res) => {
              resolve(res);
            })
            .catch((err) => {
              reject(err);
            });
          },
          fail: (err) => {
            reject(err);
          },
        });
      });
    });
    Promise.all(promises)
    .then(() => {
      params.success(200);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      if (status === 401) {
        deleteSimulatorsRetry401(authService, simulators, params);
      } else {
        params.fail(id, status);
      }
    });
  } else {
    const promises = simulators.map((simulator) => {
      const { projectid, simulatorid } = simulator;
      return AxiosDelete(WebAPI.DELETE_SIMULATOR(projectid, simulatorid));
    });
    Promise.all(promises)
      .then(() => {
        params.success(200);
      })
      .catch((err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      });
  }
}

/**
 * シミュレータを削除します
 * @function deleteSimulatorsRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {ISelectSimulatorParam[]} simulators - 選択シミュレータパラメータ配列
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function deleteSimulatorsRetry401(
  authService: AuthService,
  simulators: ISelectSimulatorParam[],
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      const promises = simulators.map((simulator) => {
        const { projectid, simulatorid } = simulator;
        const url = WebAPI.DELETE_SIMULATOR(projectid, simulatorid);
        return new Promise((resolve, reject) => {
          AxiosDelete(url, token)
            .then((res) => {
              resolve(res);
            })
            .catch((err) => {
              reject(err);
            });
        });
      });
      Promise.all(promises)
        .then(() => {
          params.success(200);
        })
        .catch((err) => {
          const { id, status } = getErrorStatus(err);
          params.fail(id, status);
        });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}
